import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useSession } from '@diggens/shared-hooks';
import { useCurrentUser, useAuthStore } from '@diggens/user-management';

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { data: session, isLoading: sessionLoading } = useSession();
  const { data: user, isLoading: userLoading } = useCurrentUser();

  // Initialize AuthStore once we have a valid session (session is the FirebaseUser)
  useEffect(() => {
    if (session && !user && !userLoading) {
      useAuthStore.getState().initialize();
    }
  }, [session, user, userLoading]);

  if (sessionLoading || userLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // No session - redirect to login, remembering where they were going (a lesson link from Slack lands on the lesson, not the home page)
  if (!session) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  // Session exists but no user data yet - still loading
  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Check if user is pending approval
  if (user.status === 'pending') {
    return <Navigate to="/pending-approval" replace />;
  }

  // Check if user is rejected
  if (user.status === 'rejected') {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
